/**
 * @fileoverview Firebase Remote Config synchronization service.
 * Fetches dynamic environment endpoints (production vs. testing backend/frontend URLs)
 * and active application build numbers.
 */

import { fetchAndActivate, getRemoteConfig, getValue } from "firebase/remote-config";
import { app } from "../notification/firebase";
import packageInfo from "../../package.json";

export const remoteConfig = getRemoteConfig(app);

const defaultConfigValues = {
  test_backend: "https://testapp.memotrack.net",
  backend: "https://app.memotrack.net",
  build_number: "39",
  version: "3.1.2"
};

// Optional: Set default values for when we are offline or before config fetches
remoteConfig.defaultConfig = defaultConfigValues;

/**
 * Local application version and build metadata.
 * Uses package.json with support for environment variable overrides.
 */
export const localAppInfo = {
  version: process.env.REACT_APP_VERSION || packageInfo.version || defaultConfigValues.version,
  buildNumber: String(
    process.env.REACT_APP_BUILD_NUMBER ||
    packageInfo.build_number ||
    packageInfo.buildNumber ||
    defaultConfigValues.build_number
  )
};

/**
 * Helper to determine if we are in a testing environment based on version matching.
 * Compares local version and build number with remote config parameters.
 * If local version and build number match remote config, it is treated as production;
 * otherwise, it switches to testing endpoints.
 *
 * @function isTestingEnvironment
 * @param {string} [remoteVersion] - Remote version to compare against.
 * @param {string|number} [remoteBuildNumber] - Remote build number to compare against.
 * @returns {boolean} True if in testing environment (version/build mismatch), false if production.
 */
export const isTestingEnvironment = (remoteVersion, remoteBuildNumber) => {
  const targetVersion = remoteVersion || (resolvedConfig && resolvedConfig.version) || defaultConfigValues.version;
  const targetBuildNumber = remoteBuildNumber !== undefined && remoteBuildNumber !== null
    ? String(remoteBuildNumber)
    : String((resolvedConfig && resolvedConfig.buildNumber) || defaultConfigValues.build_number);

  const isProduction = (
    String(localAppInfo.version) === String(targetVersion) &&
    String(localAppInfo.buildNumber) === String(targetBuildNumber)
  );

  return !isProduction;
};

const isInitialTest = isTestingEnvironment(defaultConfigValues.version, defaultConfigValues.build_number);

let resolvedConfig = {
  backendUrl: isInitialTest ? defaultConfigValues.test_backend : defaultConfigValues.backend,
  buildNumber: defaultConfigValues.build_number,
  version: defaultConfigValues.version
};

const configListeners = new Set();

/**
 * Updates the internal resolved configuration cache and notifies registered listeners.
 *
 * @function updateResolvedConfig
 * @param {Object} [nextConfig={}] - New config values to merge.
 * @returns {void}
 */
const updateResolvedConfig = (nextConfig = {}) => {
  const fallbackBackend = isTestingEnvironment(
    nextConfig.version || defaultConfigValues.version,
    nextConfig.buildNumber || defaultConfigValues.build_number
  ) ? defaultConfigValues.test_backend : defaultConfigValues.backend;

  resolvedConfig = {
    backendUrl: nextConfig.backendUrl || fallbackBackend,
    buildNumber: nextConfig.buildNumber || defaultConfigValues.build_number,
    version: nextConfig.version || defaultConfigValues.version
  };

  configListeners.forEach((listener) => listener(resolvedConfig));
};

/**
 * Subscribes a listener callback to remote configuration updates.
 *
 * @function subscribeToRemoteConfig
 * @param {Function} listener - Callback function receiving updated configuration.
 * @returns {Function} Unsubscribe cleanup function.
 */
export const subscribeToRemoteConfig = (listener) => {
  configListeners.add(listener);
  return () => configListeners.delete(listener);
};

/**
 * Initializes and fetches remote config parameters from Firebase.
 * Matches local version and build number against remote config parameters
 * to decide whether to activate production or test endpoints.
 *
 * @async
 * @function initRemoteConfig
 * @returns {Promise<void>}
 */
export const initRemoteConfig = async () => {
  try {
    // In development mode, you might want to lower the fetch interval to get updates faster.
    if (process.env.NODE_ENV === "development") {
      remoteConfig.settings.minimumFetchIntervalMillis = 36000; // 1 hour for dev, or lower
    }

    await fetchAndActivate(remoteConfig);

    const remoteVersion = getValue(remoteConfig, "version").asString() || defaultConfigValues.version;
    const remoteBuildNumber = getValue(remoteConfig, "build_number").asString() || defaultConfigValues.build_number;

    const isTest = isTestingEnvironment(remoteVersion, remoteBuildNumber);

    const nextConfig = {
      backendUrl: isTest
        ? (getValue(remoteConfig, "test_backend").asString() || defaultConfigValues.test_backend)
        : (getValue(remoteConfig, "backend").asString() || defaultConfigValues.backend),
      buildNumber: remoteBuildNumber,
      version: remoteVersion
    };

    updateResolvedConfig(nextConfig);
    console.log("Remote config fetched and activated", nextConfig, {
      localAppInfo,
      isTest
    });
  } catch (error) {
    console.error("Failed to fetch remote config:", error);
    updateResolvedConfig();
  }
};

/**
 * Retrieve the correct configuration based on the environment.
 */
export const AppConfig = {
  get backendUrl() {
    return resolvedConfig.backendUrl;
  },
  get buildNumber() {
    return resolvedConfig.buildNumber;
  },
  get version() {
    return resolvedConfig.version;
  },
  get localVersion() {
    return localAppInfo.version;
  },
  get localBuildNumber() {
    return localAppInfo.buildNumber;
  },
  get isTesting() {
    return isTestingEnvironment();
  }
};
